import React, { useState, useEffect } from "react";
import ModuleHeader from "../../moduleHeader";
import { getHoroscopeMonthly, getHoroscopeWeekly } from "../../../services/horoscopeService";
import { getCurrentPackageUserModule } from "../../../services/api";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiHeart,
  FiBriefcase,
  FiActivity,
  FiDollarSign,
  FiStar,
  FiSun,
  FiMoon
} from "react-icons/fi";

const decodeHtmlEntities = (text) => {
  if (!text) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const CATEGORY_CONFIG = {
  general: {
    icon: <FiStar className="w-5 h-5" />,
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50 text-purple-600"
  },
  love: {
    icon: <FiHeart className="w-5 h-5" />,
    color: "from-pink-500 to-rose-500",
    bgColor: "bg-pink-50 text-pink-600"
  },
  career: {
    icon: <FiBriefcase className="w-5 h-5" />,
    color: "from-blue-500 to-cyan-500",
    bgColor: "bg-blue-50 text-blue-600"
  },
  health: {
    icon: <FiActivity className="w-5 h-5" />,
    color: "from-green-500 to-emerald-500",
    bgColor: "bg-green-50 text-green-600"
  },
  finance: {
    icon: <FiDollarSign className="w-5 h-5" />,
    color: "from-yellow-500 to-orange-500",
    bgColor: "bg-yellow-50 text-yellow-600"
  },
  advice: {
    icon: <FiSun className="w-5 h-5" />,
    color: "from-teal-500 to-cyan-500",
    bgColor: "bg-teal-50 text-teal-600"
  }
};

function Horoscope({ id, moduleId, isOwner }) {
  const [selectedPeriod, setSelectedPeriod] = useState("Haftalık");
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  // Paket ve kullanıcı bilgilerini çek
  useEffect(() => {
    if (!id) return;

    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(id, 8);
        setPackageData(data);
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackageData();
  }, [id]);

  // Horoscope verisini çek
  useEffect(() => {
    const fetchHoroscope = async () => {
      try {
        const weeklyResponse = await getHoroscopeWeekly();
        const monthlyResponse = await getHoroscopeMonthly();

        setHoroscopeData({
          weekly: weeklyResponse,
          monthly: monthlyResponse
        });
      } catch (error) {
        console.error("Horoskop verisi alınamadı:", error);
      }
    };

    fetchHoroscope();
  }, []);

  if (loading || !packageData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  const formatHoroscopeData = (periodData, type) => {
    if (!periodData || !periodData.comments) return [];

    const categories = [
      { key: 'general', title: 'Genel' },
      { key: 'love', title: 'Aşk' },
      { key: 'career', title: 'Kariyer' },
      { key: 'health', title: 'Sağlık' },
      { key: 'finance', title: 'Finans' },
      { key: 'advice', title: 'Tavsiye' }
    ];

    return [{
      date: periodData.period?.description || periodData.period?.type_text || '',
      title: `${periodData.zodiac_sign?.name || ''} Burcu İçin ${periodData.period?.type_text || (type === 'weekly' ? 'Haftalık' : 'Aylık')} Yorum`,
      categories: categories
        .filter(cat => periodData.comments[cat.key] && periodData.comments[cat.key].trim() !== '')
        .map(cat => ({
          key: cat.key,
          title: cat.title,
          content: decodeHtmlEntities(periodData.comments[cat.key])
        }))
    }];
  };

  const currentData = horoscopeData
    ? formatHoroscopeData(
      selectedPeriod === "Haftalık" ? horoscopeData.weekly : horoscopeData.monthly,
      selectedPeriod === "Haftalık" ? 'weekly' : 'monthly'
    )
    : [];

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {packageData && (
          <div className="relative z-30 w-full mb-2">
            <ModuleHeader
              userName={
                packageData.user?.first_name + " " + packageData.user?.last_name
              }
              subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
              title={packageData.package?.name}
              description={packageData.package?.description}
              backgroundImage={packageData.package?.image_url}
              onExpandedChange={setIsHeaderExpanded}
              showToggle={true}
              autoHeight={true}
            />
          </div>
        )}

        <div className="min-h-screen bg-white w-full">
          <div className="px-6 pt-3 sticky top-0 z-20 bg-white/80 backdrop-blur-md pb-4">
            <div className="flex justify-center">
              <div className="relative bg-gray-100 rounded-full p-1 shadow-inner" style={{ width: '223px', height: '40px' }}>
                <div
                  className={`absolute top-1 rounded-full transition-all duration-300 ease-spring ${selectedPeriod === "Haftalık" ? "left-1" : "left-[113px]"
                    }`}
                  style={{
                    width: '109px',
                    height: '32px',
                    backgroundColor: '#3DA7B5',
                    boxShadow: '0 2px 10px rgba(61, 167, 181, 0.3)'
                  }}
                />
                <div className="relative flex h-full">
                  <button
                    onClick={() => setSelectedPeriod("Haftalık")}
                    className={`flex-1 text-sm font-medium rounded-full transition-all duration-200 flex items-center justify-center ${selectedPeriod === "Haftalık"
                      ? "text-white z-10"
                      : "text-gray-500 hover:text-gray-900"
                      }`}
                  >
                    Haftalık
                  </button>
                  <button
                    onClick={() => setSelectedPeriod("Aylık")}
                    className={`flex-1 text-sm font-medium rounded-full transition-all duration-200 flex items-center justify-center ${selectedPeriod === "Aylık"
                      ? "text-white z-10"
                      : "text-gray-500 hover:text-gray-900"
                      }`}
                  >
                    Aylık
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 pb-24 space-y-8">
            <AnimatePresence mode="wait">
              {currentData.length > 0 ? (
                currentData.map((item, index) => (
                  <motion.div
                    key={`${selectedPeriod}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                    className="space-y-6"
                  >
                    <div className="text-center space-y-1 py-2">
                      <span className="inline-block px-3 py-1 rounded-full bg-cyan-50 text-[#3DA7B5] text-xs font-semibold tracking-wider uppercase">
                        {item.date}
                      </span>
                    </div>

                    <div className="grid gap-4">
                      {item.categories && item.categories.map((category, cIndex) => {
                        const style = CATEGORY_CONFIG[category.key] || CATEGORY_CONFIG.general;

                        return (
                          <motion.div
                            key={category.key}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: cIndex * 0.1 }}
                            className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-md transition-shadow duration-300 relative overflow-hidden group"
                          >
                            <div className={`absolute -top-1 -right-1 p-4 rounded-bl-3xl ${style.bgColor} opacity-20 group-hover:opacity-30 transition-opacity duration-300`}>
                              <div className="invisible">{style.icon}</div>
                            </div>
                            <div className={`absolute top-4 right-4 p-2.5 rounded-xl ${style.bgColor} shadow-sm z-10`}>
                              {style.icon}
                            </div>
                            <div className="space-y-3 pr-10">
                              <h3 className="text-gray-900 font-black text-sm uppercase tracking-wider flex items-center gap-2">
                                {category.title}
                              </h3>
                              <p className="text-gray-600 text-[13px] leading-relaxed font-light text-left">
                                {category.content}
                              </p>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                ))
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                    <FiStar className="w-8 h-8 text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-medium">Buralar biraz sessiz...</p>
                  <p className="text-gray-400 text-sm">Bu dönem için yorum bulunamadı.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Horoscope;

